// F8 (fase 2, corte 6, ticket #0745) — la visita tiene fecha.
//
// El jefe de proyecto (o el gerente: el carril que reasigna) agenda la
// visita del contratista: un DÍA de calendario de America/Santiago y un
// RANGO de horas de ese mismo día (inicio y término, en pasos de 30
// minutos, el término después del inicio). Corte de ajustes (founder,
// 2026-10-16): el rango reemplaza a los bloques fijos («en la mañana»…).
// Vive en `findings` (`observation_visit_date`, `observation_visit_start_time`,
// `observation_visit_end_time`) y se borra en los mismos puntos que el
// plazo del contratista (`observation_due_at`), más al reasignar. Sólo para
// recetas con contraparte EXTERNA (postventa): facility no agenda.
//
// Las horas son HORA DE PARED de Santiago («09:00»), nunca un instante:
// día + hora local no tienen zona que convertir, así que no hay forma de
// que un cambio de horario (DST) o un cliente en otra zona corra la visita.
//
// Es la PRIMERA fecha que el propietario ve de la asignación: el plazo del
// contratista sigue siendo interno (ADR-0140, nota del corte 7) y no tiene
// nada que ver con esta fecha.
import { z } from "zod";
import { addCalendarDays, santiagoDate, santiagoWallInstant } from "./observation-closure-clock.js";
/** El paso de las horas de la visita, en minutos. */
export const OBSERVATION_VISIT_TIME_STEP_MINUTES = 30;
const VISIT_TIME_RE = /^([01]\d|2[0-3]):(00|30)$/;
/** ¿Es «HH:MM» una hora de la visita? 00:00 a 23:30, en pasos de 30 min. */
export function isVisitTime(time) {
    return VISIT_TIME_RE.test(time);
}
/** Una hora de la visita en el wire: «HH:MM», en pasos de 30 minutos. */
export const observationVisitTimeSchema = z
    .string()
    .refine(isVisitTime, { message: "time must be HH:MM in 30-minute steps" });
/** Postgres `time` vuelve como «09:00:00»: la hora del wire es «09:00».
 *  Cualquier otra forma se devuelve tal cual (el schema la rechazará). */
export function visitTimeFromDb(time) {
    return /^\d{2}:\d{2}:00$/.test(time) ? time.slice(0, 5) : time;
}
/** ¿El término va después del inicio? Ambas «HH:MM» del mismo día:
 *  ordenan lexicográficamente igual que cronológicamente. */
export function visitRangeValid(startTime, endTime) {
    return isVisitTime(startTime) && isVisitTime(endTime) && endTime > startTime;
}
/** «09:00» → «9:00», como se dice una hora en una frase. */
export function visitTimeLabel(time) {
    const m = /^(\d{2}):(\d{2})/.exec(time);
    if (!m)
        return time;
    return `${Number(m[1])}:${m[2]}`;
}
/** «9:00 a 13:00». */
export function visitRangeLabel(startTime, endTime) {
    return `${visitTimeLabel(startTime)} a ${visitTimeLabel(endTime)}`;
}
/** `finding_events.event_type` de cada cambio de la visita. Nota nula: la
 *  fecha vigente está en la observación; el hito sólo dice que cambió. */
export const VISIT_SCHEDULED_EVENT_TYPE = "visit_scheduled";
export const VISIT_RESCHEDULED_EVENT_TYPE = "visit_rescheduled";
export const OBSERVATION_VISIT_EVENT_TYPES = [
    VISIT_SCHEDULED_EVENT_TYPE,
    VISIT_RESCHEDULED_EVENT_TYPE,
];
/** Hasta cuántos días corridos hacia adelante se puede agendar. */
export const OBSERVATION_VISIT_MAX_DAYS_AHEAD = 180;
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** ¿Es «AAAA-MM-DD» un día real del calendario? */
export function isCalendarDate(date) {
    if (!ISO_DAY_RE.test(date))
        return false;
    const [y, m, d] = date.split("-").map(Number);
    const t = new Date(Date.UTC(y, m - 1, d));
    return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}
/** El día de la visita, en el wire: «AAAA-MM-DD» válido. */
export const observationVisitDateSchema = z
    .string()
    .refine(isCalendarDate, { message: "date must be a calendar day (YYYY-MM-DD)" });
/**
 * ¿Se puede agendar ese día? Desde hoy (en Santiago) hasta
 * `OBSERVATION_VISIT_MAX_DAYS_AHEAD` días corridos. Un día pasado nunca:
 * el propietario leería una visita que ya no va a ocurrir.
 */
export function visitDateSchedulable(date, now) {
    if (!isCalendarDate(date))
        return false;
    const today = santiagoDate(now);
    // «AAAA-MM-DD» ordena lexicográficamente igual que cronológicamente.
    return date >= today && date <= addCalendarDays(today, OBSERVATION_VISIT_MAX_DAYS_AHEAD);
}
/** ¿Cambió la visita? Mismo día y mismo rango = no hay nada que avisar. */
export function visitChanged(before, after) {
    return (before.date !== after.date ||
        before.startTime !== after.startTime ||
        before.endTime !== after.endTime);
}
// ADR-0160 (fase 3, ola 2, #0762) — el contratista agenda o cambia la
// visita desde su enlace (`routes/public-visit.ts` de core-api). La visita
// que escribe queda «por confirmar» con el propietario; la confirmación
// (confirmar, pedir otro horario, auto-confirmar a las 48 h) es la próxima
// ola, que usa los estados ya reservados abajo sin migrar.
/** `findings.observation_visit_confirmation`: el estado de la visita
 *  VIGENTE cuando la propuso el contratista. NULL = sin visita, o la
 *  escribió el personal. En este corte sólo se escribe `pending`; los
 *  otros tres quedan reservados (están en el CHECK) para la próxima ola. */
export const OBSERVATION_VISIT_CONFIRMATIONS = [
    "pending",
    "confirmed",
    "other_time_requested",
    "auto_confirmed",
];
/** `observation_visit_proposals.proposed_by`: quién escribió esa fecha. */
export const OBSERVATION_VISIT_PROPOSERS = ["staff", "contractor"];
/** Escrituras del contratista por ENLACE, contando la primera (agendar +
 *  dos cambios). Pasado el tope, debe hablar con la inmobiliaria. */
export const OBSERVATION_VISIT_CONTRACTOR_MAX_CHANGES = 3;
/** Hasta cuántos días corridos hacia adelante agenda el CONTRATISTA (el
 *  personal sigue con `OBSERVATION_VISIT_MAX_DAYS_AHEAD`). */
export const OBSERVATION_VISIT_CONTRACTOR_MAX_DAYS_AHEAD = 60;
const WALL_TIME_FORMAT = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Santiago",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
});
/** «HH:MM» — la hora de pared de un instante en Santiago. */
export function santiagoWallTime(now) {
    return WALL_TIME_FORMAT.format(now);
}
/**
 * ¿Puede el CONTRATISTA agendar esa visita? Desde hoy (Santiago) hasta
 * `OBSERVATION_VISIT_CONTRACTOR_MAX_DAYS_AHEAD` días corridos y, si el día
 * es hoy, con un inicio posterior a la hora de pared actual: cierra, sólo
 * para el contratista, el hueco de `visitDateSchedulable`, que acepta
 * horas ya pasadas de hoy. El rango (pasos y término > inicio) lo valida
 * el schema del body.
 */
export function contractorVisitCheck(v, now) {
    if (!isCalendarDate(v.date))
        return "date_out_of_range";
    const today = santiagoDate(now);
    if (v.date < today ||
        v.date > addCalendarDays(today, OBSERVATION_VISIT_CONTRACTOR_MAX_DAYS_AHEAD))
        return "date_out_of_range";
    if (v.date === today && v.startTime <= santiagoWallTime(now))
        return "time_passed";
    // ADR-0161: el propietario necesita tiempo para confirmar (decisión 1
    // del founder, 2026-10-17).
    if (visitStartInstant(v).getTime() - now.getTime() <
        OBSERVATION_VISIT_CONTRACTOR_MIN_LEAD_HOURS * HOUR_MS)
        return "too_soon";
    return "ok";
}
/** ¿La visita cae DESPUÉS del día del plazo de la observación (en
 *  Santiago)? Es una advertencia, nunca un bloqueo. Sin plazo, no. */
export function visitAfterDue(date, dueAt) {
    return dueAt !== null && date > santiagoDate(dueAt);
}
// ADR-0161 (fase 3, ola 3, #0768) — el propietario confirma la visita que
// propuso el contratista o pide otro horario; si no responde, se confirma
// sola. Decisiones del founder (2026-10-17):
//   1. la hora límite es min(correo enviado + 48 h, inicio − 12 h), y el
//      contratista no propone con menos de 24 h de anticipación;
//   2. el plazo corre desde que el correo de la propuesta SALIÓ (constancia);
//      sin constancia, la visita nunca se confirma sola;
//   3. «otro horario» es estructurado: días + una franja, sin texto libre;
//   4. un grupo (misma unidad, contratista, día y rango) = una sola visita.
const HOUR_MS = 3_600_000;
/** Horas desde que salió el correo de la propuesta hasta que se confirma
 *  sola. */
export const OBSERVATION_VISIT_AUTO_CONFIRM_HOURS = 48;
/** …pero nunca después de `inicio − 12 h`. */
export const OBSERVATION_VISIT_AUTO_CONFIRM_BEFORE_START_HOURS = 12;
/** El contratista no propone visitas que empiecen antes de esto. */
export const OBSERVATION_VISIT_CONTRACTOR_MIN_LEAD_HOURS = 24;
/** Margen mínimo entre el correo que salió y la hora límite: si un
 *  reintento tardío deja menos, el correo sale SIN hora límite y la visita
 *  no se confirma sola. Una hora MENOS que las 12 que garantiza la
 *  anticipación de 24 h (24 − 12): el primer envío de una propuesta
 *  aceptada justo a 24 h se reclama segundos después y tiene que seguir
 *  prometiendo. */
export const OBSERVATION_VISIT_MIN_OWNER_NOTICE_HOURS = 11;
/** Los hitos de la respuesta. NO entran en `OBSERVATION_VISIT_EVENT_TYPES`:
 *  esa lista dice «cambió la fecha» (la última visita, el reinicio del
 *  pedido de visita), y una respuesta no cambia la fecha. */
export const VISIT_CONFIRMED_EVENT_TYPE = "visit_confirmed";
export const VISIT_OTHER_TIME_REQUESTED_EVENT_TYPE = "visit_other_time_requested";
export const VISIT_AUTO_CONFIRMED_EVENT_TYPE = "visit_auto_confirmed";
export const OBSERVATION_VISIT_RESPONSE_EVENT_TYPES = [
    VISIT_CONFIRMED_EVENT_TYPE,
    VISIT_OTHER_TIME_REQUESTED_EVENT_TYPE,
    VISIT_AUTO_CONFIRMED_EVENT_TYPE,
];
/** `observation_visit_responses.response`. */
export const OBSERVATION_VISIT_RESPONSES = [
    "confirmed",
    "other_time_requested",
    "auto_confirmed",
];
/** La franja de «otro horario»: una para todos los días marcados. */
export const OBSERVATION_VISIT_SLOTS = ["morning", "afternoon", "any"];
/** Franja = hora de INICIO de la visita, en hora de pared de Santiago,
 *  intervalo [desde, hasta). «any» no restringe la hora. */
export const OBSERVATION_VISIT_SLOT_START_WINDOWS = {
    morning: { from: "08:00", to: "13:00" },
    afternoon: { from: "13:00", to: "19:00" },
};
/** Los días que el propietario puede marcar: desde mañana hasta hoy + 14. */
export const OBSERVATION_VISIT_PREFERRED_DAYS_AHEAD = 14;
/** Cuántos días puede marcar como máximo. */
export const OBSERVATION_VISIT_PREFERRED_DAYS_MAX = 5;
/** Los días que se ofrecen, en Santiago: mañana … hoy + 14. */
export function preferredDayOptions(now) {
    const today = santiagoDate(now);
    return Array.from({ length: OBSERVATION_VISIT_PREFERRED_DAYS_AHEAD }, (_, i) => addCalendarDays(today, i + 1));
}
/** Lo que valida la ruta de «otro horario» además del schema. */
export function preferredDatesCheck(dates, now) {
    if (dates.length === 0)
        return "empty";
    if (dates.length > OBSERVATION_VISIT_PREFERRED_DAYS_MAX)
        return "too_many";
    if (new Set(dates).size !== dates.length)
        return "duplicate";
    const options = new Set(preferredDayOptions(now));
    if (dates.some((d) => !isCalendarDate(d) || !options.has(d)))
        return "out_of_range";
    return "ok";
}
/** ¿La visita calza con lo que pidió el propietario? Sólo alimenta avisos
 *  que no bloquean: la preferencia es orientativa. */
export function visitMatchesPreference(v, p) {
    if (!p.dates.includes(v.date))
        return false;
    if (p.slot === "any")
        return true;
    const w = OBSERVATION_VISIT_SLOT_START_WINDOWS[p.slot];
    const start = v.startTime.slice(0, 5);
    return start >= w.from && start < w.to;
}
/** El instante en que empieza la visita (hora de pared de Santiago). */
export function visitStartInstant(v) {
    return santiagoWallInstant(v.date, v.startTime);
}
/**
 * La hora límite que promete el correo de la propuesta, contada desde que
 * se reclamó el envío: min(+48 h, inicio − 12 h). Null si deja menos de
 * `OBSERVATION_VISIT_MIN_OWNER_NOTICE_HOURS` para responder: ese correo
 * sale sin hora límite y la visita no se confirma sola. Instantes, nunca
 * aritmética de pared.
 */
export function visitAutoConfirmPromise(input) {
    const claimed = input.claimedAt.getTime();
    const byNotice = claimed + OBSERVATION_VISIT_AUTO_CONFIRM_HOURS * HOUR_MS;
    const byStart = visitStartInstant(input.visit).getTime() -
        OBSERVATION_VISIT_AUTO_CONFIRM_BEFORE_START_HOURS * HOUR_MS;
    const deadline = Math.min(byNotice, byStart);
    if (deadline - claimed < OBSERVATION_VISIT_MIN_OWNER_NOTICE_HOURS * HOUR_MS)
        return null;
    return new Date(deadline);
}
/**
 * La hora límite de un grupo: el MÁXIMO de las de sus miembros (un aviso
 * tardío nunca acorta el plazo de los anteriores), o null si a alguno le
 * falta. Todos comparten el inicio, así que el tope de −12 h es común.
 */
export function visitGroupDeadline(memberPromises) {
    if (memberPromises.length === 0)
        return null;
    let max = null;
    for (const p of memberPromises) {
        if (p === null)
            return null;
        if (max === null || p.getTime() > max.getTime())
            max = p;
    }
    return max;
}
//# sourceMappingURL=observation-visit.js.map